import React from 'react'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import '@fontsource/poppins/400.css'
import '@fontsource/poppins/500.css'
import '@fontsource/poppins/600.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'

const palette = {
  background: {
    default: '#F8FAFC',
    paper: '#FFFFFF',
  },

  primary: {
    main: '#242F40',
    light: '#4A5568', // lighter version of primary
    dark: '#1A2230', // darker version
    contrastText: '#FFFFFF',
  },

  secondary: {
    main: '#3A475C',
    light: '#5C6A80',
    dark: '#2A3445',
    contrastText: '#FFFFFF',
  },

  third: {
    main: '#239AA4',
    dark: '#1E7F88',
    light: '#4DBBC4',
    contrastText: '#FFFFFF',
  },

  contrast: {
    main: '#239AA4',
    dark: '#1E7F88',
    light: '#4DBBC4',
    contrastText: '#FFFFFF',
  },

  anWhite: {
    main: '#FFFFFF',
    contrastText: '#242F40',
  },

  anBackground: {
    main: '#F8FAFC',
    contrastText: '#242F40',
  },

  anMuted: {
    main: '#F1F5F9',
    contrastText: '#242F40',
  },

  highlight: {
    main: '#CCA43B',
    contrastText: '#242F40',
  },

  greycontrast: {
    main: '#E2E8F0',
    contrastText: '#242F40',
  },

  anMainBlue: {
    main: '#268EBB',
    contrastText: '#FFFFFF',
  },

  favourite: {
    contrastText: '#b4a005'
  },

  anRed: {
    main: '#E32525',
    contrastText: '#FFFFFF',
  },
  anOrange: {
    main: '#F28B1F',
    contrastText: '#242F40',
  },
  anYellow: {
    main: '#FADF08',
    contrastText: '#242F40',
  },
  anGreen: {
    main: '#00964C',
    contrastText: '#FFFFFF',
  },
  anBlue: {
    main: '#1F3D7C',
    contrastText: '#FFFFFF',
  },
  anPurple: {
    main: '#85276A',
    contrastText: '#FFFFFF',
  },

  text: {
    primary: '#242F40',
    secondary: '#64748B',
    disabled: '#94A3B8',
  },

  divider: '#E2E8F0',

  success: {
    main: '#00964C', // your green
    light: '#4ADE80',
    dark: '#065F46',
  },

  warning: {
    main: '#F28B1F', // your orange
    light: '#FDBA74',
    dark: '#9A3412',
  },

  error: {
    main: '#E32525', // your red
    light: '#F87171',
    dark: '#7F1D1D',
  },
  info: {
    main: '#268EBB',
    contrastText: '#FFFFFF',
  },
}

const typography = {
  fontFamily: '"Poppins", "Roboto", "Arial", sans-serif',

  h1: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },
  h2: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },
  h3: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },
  h4: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },
  h5: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },
  h6: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    color: '#242F40',
  },

  body1: {
    fontFamily: 'Poppins, sans-serif',
    color: '#242F40',
  },
  body2: {
    fontFamily: 'Poppins, sans-serif',
    color: '#64748B',
  },

  button: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    textTransform: 'none',
  },

  bigNumber: {
    color: '#242F40',
    fontSize: '4rem',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
  },
  title: {
    fontSize: '1rem',
    color: '#242F40',
    fontWeight: 600,
    fontFamily: 'Poppins, sans-serif',
  },
  title2: {
    fontSize: '1.5rem',
    color: '#334155',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 500,
  },
  digital: {
    fontFamily: 'digital',
  },
  title3: {
    color: '#3E4E50',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '1.1rem',
    fontWeight: 500,
  },
  title4: {
    color: '#3D4E50',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: '1.1rem',
    paddingBottom: 5,
    paddingTop: 10,
  },
  smallGrey: {
    fontSize: '0.8rem',
    color: '#64748B',
    fontFamily: 'Poppins, sans-serif',
  },
}

const components = {
  MuiCssBaseline: {
    styleOverrides: {
      body: {
        backgroundColor: '#F8FAFC',
      },
    },
  },

  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 5,
        margin: 0,
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow:
          '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 8px rgba(15, 23, 42, 0.06)',
        overflow: 'hidden',

        '&:before': {
          display: 'none',
        },

        '&.Mui-expanded': {
          margin: 0,
        },

        '&:not(:last-child)': {
          marginBottom: 16,
        },
      }),
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        minHeight: 60,
        paddingLeft: 20,
        paddingRight: 20,
        '&.Mui-expanded': {
          minHeight: 60,
        },
      },
      content: {
        margin: '10px 0',
        '&.Mui-expanded': {
          margin: '10px 0',
        },
      },
      expandIconWrapper: {
        transition: 'transform 0.2s ease',
        color: '#64748B',
        '&.Mui-expanded': {
          transform: 'rotate(180deg)',
        },
      },
    },
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: '16px 20px 20px 20px',
        borderTop: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#242F40',
        color: '#FFFFFF',
        boxShadow: 'none',
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => ({
        borderRadius: 5,
        boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)',
        border: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        backgroundImage: 'none',
      },
      outlined: ({ theme }) => ({
        borderColor: theme.palette.divider,
      }),
    },
  },

  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 5,
        paddingLeft: 16,
        paddingRight: 16,
        boxShadow: 'none',
      },
      containedPrimary: {
        backgroundColor: '#239AA4',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#1E7F88',
          boxShadow: 'none',
        },
      },
      containedSecondary: {
        backgroundColor: '#3A475C',
        color: '#FFFFFF',
        '&:hover': {
          backgroundColor: '#2F3C50',
          boxShadow: 'none',
        },
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      head: {
        backgroundColor: '#242F40',
        color: '#FFFFFF',
        fontWeight: 600,
      },
      body: ({ theme }) => ({
        borderBottom: `1px solid ${theme.palette.divider}`,
      }),
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 5,
        fontWeight: 500,
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: ({ theme, ownerState }) => ({
        '&:hover fieldset': {
          borderColor: theme.palette.contrast.main,
        },

        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: theme.palette.contrast.main,
          borderWidth: '1.6px',
        },

        '&:has(input:not(:placeholder-shown))': {
          backgroundColor: theme.palette.anWhite.main,
        },

        '&:has(textarea:not(:placeholder-shown))': {
          backgroundColor: theme.palette.anWhite.main,
        },
      }),
    },
  },
}

const ThemeSchema = createTheme({
  palette,
  typography,
  components,
  shape: {
    borderRadius: 5,
  },
})

const AmbuNetTheme = ({ children }) => {
  return <ThemeProvider theme={ThemeSchema}>{children}</ThemeProvider>
}

export default AmbuNetTheme
